const INDIAN_FORMAT = new Intl.NumberFormat("en-IN");

/**
 * Compact formatting rules (shared across admin dashboard):
 *   < 1,000         -> "1,234" (en-IN comma separated)
 *   < 10 Lakh       -> "1K" / "999K"
 *   < 100 Lakh      -> "1M" / "9.9M"
 *   >= 100 Lakh     -> "1Cr" / "10Cr"
 *
 * If rounding pushes a value into the next magnitude (e.g. "1000K"),
 * we bump it to the next suffix (e.g. "1M") so we never display "1000K".
 */
const MAGNITUDES: Array<{ threshold: number; suffix: string }> = [
  { threshold: 10_000_000, suffix: "Cr" },
  { threshold: 1_000_000, suffix: "M" },
  { threshold: 1_000, suffix: "K" },
];

function pickMagnitude(abs: number): { threshold: number; suffix: string } {
  for (const m of MAGNITUDES) {
    if (abs >= m.threshold) return m;
  }
  return { threshold: 1, suffix: "" };
}

function nextSuffix(suffix: string): string | null {
  const idx = MAGNITUDES.findIndex((m) => m.suffix === suffix);
  if (idx <= 0) return null;
  return MAGNITUDES[idx - 1].suffix;
}

function formatScaled(
  n: number,
  suffix: string,
  threshold: number,
): string {
  const v = n / threshold;
  const decimals = v >= 100 ? 0 : 1;
  let str = v.toFixed(decimals).replace(/\.0$/, "");

  const overflow = parseFloat(str) >= 1000;
  if (overflow) {
    const ns = nextSuffix(suffix);
    if (ns) {
      const nextThreshold = threshold * 1000;
      return formatScaled(n, ns, nextThreshold);
    }
    str = (parseFloat(str) / 1000).toString();
  }
  return str + suffix;
}

export function formatCompactCount(value: number): string {
  if (!Number.isFinite(value)) return "0";
  const n = Number(value);
  const abs = Math.abs(n);
  const m = pickMagnitude(abs);
  if (m.suffix === "") return INDIAN_FORMAT.format(n);
  return formatScaled(n, m.suffix, m.threshold);
}

export function formatCompactCurrency(value: number): string {
  if (!Number.isFinite(value)) return "₹0";
  const n = Number(value);
  const abs = Math.abs(n);
  const m = pickMagnitude(abs);
  if (m.suffix === "") return `₹${INDIAN_FORMAT.format(n)}`;
  return `₹${formatScaled(n, m.suffix, m.threshold)}`;
}

export function formatIndianNumber(value: number): string {
  if (!Number.isFinite(value)) return "0";
  return INDIAN_FORMAT.format(Number(value));
}
